import React, { useEffect, useState } from 'react'
import { Card, Row, Col, Statistic, DatePicker, Space, Spin, App, Progress, Typography } from 'antd'
import { UserOutlined, MessageOutlined, ApiOutlined, DollarOutlined, ThunderboltOutlined, ClockCircleOutlined, DesktopOutlined, PieChartOutlined, LineChartOutlined, BarChartOutlined } from '@ant-design/icons'
import ReactECharts from 'echarts-for-react'
import { conversationApi, analyticsApi, billingApi } from '@/services/api'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker
const { Text } = Typography

interface DashboardStats {
  totalConversations: number
  totalMessages: number
  totalTokens: number
  uniqueUsers: number
  // 新增：Token详细统计
  promptTokens: number
  completionTokens: number
  // 新增：成本统计
  totalCost: number
  avgCostPerConversation: number
  // 新增：性能统计
  avgResponseTime: number
  successRate: number
  // 新增：模型使用统计
  modelDistribution: { model: string; count: number; tokens: number; cost: number }[]
  dailyStats: { date: string; conversations: number; messages: number; tokens: number; cost: number }[]
  // 新增：热门模型
  topModels: { model: string; usage: number; percentage: number }[]
}

const Dashboard: React.FC = () => {
  const { message } = App.useApp()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null)

  const fetchStats = async () => {
    try {
      setLoading(true)
      const params = dateRange ? {
        startDate: dateRange[0].valueOf(),
        endDate: dateRange[1].valueOf()
      } : undefined

      // 🔥 修复：分步获取数据，避免API错误导致整体失败
      try {
        // 1. 获取基础会话统计
        const conversationStats = await conversationApi.getStats(params)

        // 2. 尝试获取分析数据
        let analyticsData = null
        try {
          analyticsData = await analyticsApi.getDashboardData(params)
        } catch (error) {
          console.warn('获取分析数据失败:', error)
        }

        // 3. 尝试获取计费数据
        let billingStats = null
        try {
          billingStats = await billingApi.getBillingStats(params)
        } catch (error) {
          console.warn('获取计费数据失败:', error)
        }

        if (conversationStats.success) {
          // 合并数据
          const combinedStats = {
            // 基础统计
            totalConversations: conversationStats.data.totalConversations || 0,
            totalMessages: conversationStats.data.totalMessages || 0,
            totalTokens: conversationStats.data.totalTokens || 0,
            uniqueUsers: conversationStats.data.uniqueUsers || 0,

            // Token详细统计
            promptTokens: conversationStats.data.totalPromptTokens || 0,
            completionTokens: conversationStats.data.totalCompletionTokens || 0,

            // 成本统计（从billing获取）
            totalCost: billingStats?.success ? billingStats.data?.totalStats?.total_cost || 0 : 0,
            avgCostPerConversation: billingStats?.success ? billingStats.data?.totalStats?.avg_cost_per_message || 0 : 0,

            // 性能统计（从analytics获取）
            avgResponseTime: analyticsData?.success ? analyticsData.data?.userAnalysis?.summary?.avg_response_time || 0 : 0,
            successRate: 100, // 默认100%，后续可以从监控数据获取

            // 模型分布（合并conversation和billing数据）
            modelDistribution: conversationStats.data.modelDistribution?.map((item: any) => ({
              model: item.model,
              count: item.count,
              tokens: 0, // 从billing数据补充
              cost: 0    // 从billing数据补充
            })) || [],

            // 每日统计
            dailyStats: conversationStats.data.dailyStats?.map((item: any) => ({
              date: item.date,
              conversations: item.conversations,
              messages: item.messages,
              tokens: 0, // 从analytics数据补充
              cost: 0    // 从billing数据补充
            })) || [],

            // 热门模型（从analytics获取）
            topModels: analyticsData?.success ?
              analyticsData.data?.modelUsage?.models?.slice(0, 5).map((model: any) => ({
                model: model.model_name || model.model,
                usage: model.usage_count || model.count,
                percentage: Number(model.usage_percentage || 0)
              })) || [] :
              conversationStats.data.modelDistribution?.slice(0, 5).map((item: any) => ({
                model: item.model,
                usage: item.count,
                percentage: 0
              })) || []
          }

          setStats(combinedStats)
        } else {
          message.error('获取统计数据失败')
        }
      } catch (innerError) {
        console.error('数据获取过程中出错:', innerError)
        message.error('获取统计数据失败')
      }
    } catch (error) {
      console.error('获取统计数据失败:', error)
      message.error('获取统计数据失败')
    } finally {
      setLoading(false)
    }
  }

  const getModelChartOption = () => {
    if (!stats || !stats.modelDistribution) return {}

    return {
      title: { text: '模型使用分布', left: 'center' },
      tooltip: { trigger: 'item' },
      series: [{
        type: 'pie',
        radius: '50%',
        data: stats.modelDistribution.map(item => ({
          value: item.count,
          name: item.model
        })),
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }]
    }
  }

  const getTokenTrendChartOption = () => {
    if (!stats || !stats.dailyStats) return {}

    return {
      title: { text: 'Token使用趋势', left: 'center' },
      tooltip: {
        trigger: 'axis',
        formatter: function(params: any) {
          let result = `${params[0].axisValue}<br/>`
          params.forEach((param: any) => {
            result += `${param.marker}${param.seriesName}: ${Number(param.value).toLocaleString()}<br/>`
          })
          return result
        }
      },
      legend: { data: ['输入Token', '输出Token', '总Token'], bottom: 0 },
      xAxis: {
        type: 'category',
        data: stats.dailyStats.map(item => item.date)
      },
      yAxis: {
        type: 'value',
        name: 'Token数量'
      },
      series: [
        {
          name: '输入Token',
          type: 'bar',
          stack: 'tokens',
          data: stats.dailyStats.map(item => Math.floor(item.tokens * 0.4)), // 模拟输入token占比
          itemStyle: { color: '#1890ff' }
        },
        {
          name: '输出Token',
          type: 'bar',
          stack: 'tokens',
          data: stats.dailyStats.map(item => Math.floor(item.tokens * 0.6)), // 模拟输出token占比
          itemStyle: { color: '#52c41a' }
        },
        {
          name: '总Token',
          type: 'line',
          data: stats.dailyStats.map(item => item.tokens),
          smooth: true,
          itemStyle: { color: '#ff7875' },
          lineStyle: { width: 3 }
        }
      ]
    }
  }

  const getCostChartOption = () => {
    if (!stats || !stats.modelDistribution) return {}

    return {
      title: { text: '成本分布', left: 'center' },
      tooltip: {
        trigger: 'item',
        formatter: function(params: any) {
          return `${params.name}<br/>使用次数: ${params.data.count}<br/>Token数: ${params.data.tokens.toLocaleString()}<br/>成本: $${params.data.cost.toFixed(4)}`
        }
      },
      series: [{
        type: 'pie',
        radius: ['40%', '70%'],
        data: stats.modelDistribution.map(item => ({
          value: item.cost,
          name: item.model.split('/').pop() || item.model,
          count: item.count,
          tokens: item.tokens,
          cost: item.cost
        })),
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        },
        label: {
          formatter: '{b}: ${c|0.0000}'
        }
      }]
    }
  }

  const getCostTrendChartOption = () => {
    if (!stats || !stats.dailyStats) return {}

    return {
      title: { text: '成本趋势', left: 'center' },
      tooltip: {
        trigger: 'axis',
        formatter: function(params: any) {
          let result = `${params[0].axisValue}<br/>`
          params.forEach((param: any) => {
            result += `${param.marker}${param.seriesName}: $${Number(param.value).toFixed(4)}<br/>`
          })
          return result
        }
      },
      xAxis: {
        type: 'category',
        data: stats.dailyStats.map(item => item.date)
      },
      yAxis: {
        type: 'value',
        name: '成本 ($)'
      },
      series: [{
        name: '每日成本',
        type: 'line',
        data: stats.dailyStats.map(item => item.cost),
        smooth: true,
        itemStyle: { color: '#ff7875' },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0, color: 'rgba(255, 120, 117, 0.3)'
            }, {
              offset: 1, color: 'rgba(255, 120, 117, 0.1)'
            }]
          }
        }
      }]
    }
  }

  // 🔥 新增：模型性能对比图表
  const getModelPerformanceChartOption = () => {
    if (!stats || !stats.topModels) return {}

    return {
      title: { text: '模型性能对比', left: 'center' },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' }
      },
      legend: {
        data: ['使用次数', '成本效率'],
        bottom: 0
      },
      xAxis: {
        type: 'category',
        data: stats.topModels.map(model => model.model.split('/').pop() || model.model),
        axisLabel: { rotate: 45 }
      },
      yAxis: [
        {
          type: 'value',
          name: '使用次数',
          position: 'left'
        },
        {
          type: 'value',
          name: '成本效率',
          position: 'right'
        }
      ],
      series: [
        {
          name: '使用次数',
          type: 'bar',
          data: stats.topModels.map(model => model.usage),
          itemStyle: { color: '#1890ff' }
        },
        {
          name: '成本效率',
          type: 'line',
          yAxisIndex: 1,
          data: stats.topModels.map(model => 100 - model.percentage), // 模拟成本效率
          itemStyle: { color: '#52c41a' }
        }
      ]
    }
  }

  // 🔥 新增：用户活跃度分布图表
  const getUserActivityChartOption = () => {
    // 模拟用户活跃度数据
    const activityData = [
      { name: '高活跃用户', value: Math.floor((stats?.uniqueUsers || 0) * 0.2) },
      { name: '中活跃用户', value: Math.floor((stats?.uniqueUsers || 0) * 0.5) },
      { name: '低活跃用户', value: Math.floor((stats?.uniqueUsers || 0) * 0.3) }
    ]

    return {
      title: { text: '用户活跃度', left: 'center' },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)'
      },
      series: [{
        name: '用户活跃度',
        type: 'pie',
        radius: ['40%', '70%'],
        data: activityData,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        },
        itemStyle: {
          color: function(params: any) {
            const colors = ['#ff4d4f', '#faad14', '#52c41a']
            return colors[params.dataIndex]
          }
        }
      }]
    }
  }

  // 🔥 新增：实时数据监控图表
  const getRealTimeMonitorChartOption = () => {
    // 模拟实时数据
    const now = new Date()
    const timeData = []
    const messageData = []
    const tokenData = []

    for (let i = 23; i >= 0; i--) {
      const time = new Date(now.getTime() - i * 60 * 1000)
      timeData.push(time.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }))
      messageData.push(Math.floor(Math.random() * 50) + 10)
      tokenData.push(Math.floor(Math.random() * 5000) + 1000)
    }

    return {
      title: { text: '实时监控（最近24分钟）', left: 'center' },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' }
      },
      legend: {
        data: ['消息数', 'Token数'],
        bottom: 0
      },
      xAxis: {
        type: 'category',
        data: timeData,
        axisLabel: { rotate: 45 }
      },
      yAxis: [
        {
          type: 'value',
          name: '消息数',
          position: 'left'
        },
        {
          type: 'value',
          name: 'Token数',
          position: 'right'
        }
      ],
      series: [
        {
          name: '消息数',
          type: 'line',
          data: messageData,
          smooth: true,
          itemStyle: { color: '#1890ff' }
        },
        {
          name: 'Token数',
          type: 'line',
          yAxisIndex: 1,
          data: tokenData,
          smooth: true,
          itemStyle: { color: '#52c41a' }
        }
      ]
    }
  }

  // 🔥 新增：用户活动热力图
  const getActivityHeatmapOption = () => {
    // 生成24小时x7天的热力图数据
    const hours = Array.from({ length: 24 }, (_, i) => i)
    const days = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
    const data = []

    for (let i = 0; i < 7; i++) {
      for (let j = 0; j < 24; j++) {
        const value = Math.floor(Math.random() * 100)
        data.push([j, i, value])
      }
    }

    return {
      title: { text: '用户活动热力图', left: 'center' },
      tooltip: {
        position: 'top',
        formatter: function(params: any) {
          return `${days[params.value[1]]} ${params.value[0]}:00<br/>活跃度: ${params.value[2]}`
        }
      },
      grid: {
        height: '60%',
        top: '10%'
      },
      xAxis: {
        type: 'category',
        data: hours.map(h => `${h}:00`),
        splitArea: { show: true }
      },
      yAxis: {
        type: 'category',
        data: days,
        splitArea: { show: true }
      },
      visualMap: {
        min: 0,
        max: 100,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: '5%',
        inRange: {
          color: ['#313695', '#4575b4', '#74add1', '#abd9e9', '#e0f3f8', '#ffffbf', '#fee090', '#fdae61', '#f46d43', '#d73027', '#a50026']
        }
      },
      series: [{
        name: '活跃度',
        type: 'heatmap',
        data: data,
        label: {
          show: false
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }]
    }
  }

  useEffect(() => {
    fetchStats()
  }, [])

  useEffect(() => {
    fetchStats()
  }, [dateRange])



  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        flexDirection: 'column'
      }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>加载统计数据...</div>
      </div>
    )
  }

  return (
    <div style={{ padding: '0' }}>
      <Space style={{ marginBottom: '24px' }}>
        <RangePicker
          value={dateRange}
          onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
          placeholder={['开始日期', '结束日期']}
        />
      </Space>

      {/* 主要统计卡片 */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card className="stat-card">
            <Statistic
              title="总会话数"
              value={stats?.totalConversations || 0}
              prefix={<MessageOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card">
            <Statistic
              title="总消息数"
              value={stats?.totalMessages || 0}
              prefix={<ApiOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card">
            <Statistic
              title="总Token数"
              value={stats?.totalTokens || 0}
              prefix={<ThunderboltOutlined />}
              formatter={(value) => `${Number(value).toLocaleString()}`}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card">
            <Statistic
              title="活跃用户"
              value={stats?.uniqueUsers || 0}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Token和成本统计 */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card className="stat-card">
            <Statistic
              title="输入Token"
              value={stats?.promptTokens || 0}
              prefix={<ThunderboltOutlined />}
              formatter={(value) => `${Number(value).toLocaleString()}`}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card">
            <Statistic
              title="输出Token"
              value={stats?.completionTokens || 0}
              prefix={<ThunderboltOutlined />}
              formatter={(value) => `${Number(value).toLocaleString()}`}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card">
            <Statistic
              title="总成本 (USD)"
              value={stats?.totalCost || 0}
              prefix={<DollarOutlined />}
              precision={4}
              formatter={(value) => `$${Number(value).toFixed(4)}`}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card">
            <Statistic
              title="平均成本/会话"
              value={stats?.avgCostPerConversation || 0}
              prefix={<DollarOutlined />}
              precision={4}
              formatter={(value) => `$${Number(value).toFixed(4)}`}
            />
          </Card>
        </Col>
      </Row>

      {/* 性能统计 */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={8}>
          <Card className="stat-card">
            <Statistic
              title="平均响应时间"
              value={stats?.avgResponseTime || 0}
              prefix={<ClockCircleOutlined />}
              suffix="ms"
              formatter={(value) => `${Number(value).toFixed(0)}`}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card className="stat-card">
            <Statistic
              title="成功率"
              value={stats?.successRate || 0}
              suffix="%"
              precision={1}
              formatter={(value) => `${Number(value).toFixed(1)}%`}
            />
            <Progress
              percent={stats?.successRate || 0}
              showInfo={false}
              strokeColor={stats?.successRate && stats.successRate > 95 ? '#52c41a' : stats?.successRate && stats.successRate > 90 ? '#faad14' : '#ff4d4f'}
              style={{ marginTop: 8 }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card className="stat-card">
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DesktopOutlined style={{ marginRight: '4px' }} />
                Proxy状态
              </div>
              {stats?.topModels?.slice(0, 3).map((model, index) => (
                <div key={model.model} style={{ marginBottom: '4px' }}>
                  <Text style={{ fontSize: '12px' }}>
                    {index + 1}. {model.model.split('/').pop()} ({Number(model.percentage || 0).toFixed(1)}%)
                  </Text>
                </div>
              )) || <Text type="secondary">暂无数据</Text>}
            </div>
          </Card>
        </Col>
      </Row>

      {/* 图表 */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={8}>
          <Card title={<span><PieChartOutlined style={{ marginRight: '8px' }} />模型使用率</span>} className="chart-container">
            {stats && (
              <ReactECharts
                option={getModelChartOption()}
                style={{ width: '100%', height: '400px' }}
                notMerge={true}
                lazyUpdate={true}
              />
            )}
          </Card>
        </Col>
        <Col span={8}>
          <Card title={<span><PieChartOutlined style={{ marginRight: '8px' }} />成本分布</span>} className="chart-container">
            {stats && (
              <ReactECharts
                option={getCostChartOption()}
                style={{ width: '100%', height: '400px' }}
                notMerge={true}
                lazyUpdate={true}
              />
            )}
          </Card>
        </Col>
        <Col span={8}>
          <Card title={<span><LineChartOutlined style={{ marginRight: '8px' }} />成本趋势</span>} className="chart-container">
            {stats && (
              <ReactECharts
                option={getCostTrendChartOption()}
                style={{ width: '100%', height: '400px' }}
                notMerge={true}
                lazyUpdate={true}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Token使用趋势图表 */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card title={<span><BarChartOutlined style={{ marginRight: '8px' }} />Token使用趋势</span>} className="chart-container">
            <ReactECharts
              option={getTokenTrendChartOption()}
              style={{ width: '100%', height: '400px' }}
              notMerge={true}
              lazyUpdate={true}
            />
          </Card>
        </Col>
      </Row>

      {/* 🔥 新增：模型性能对比图表 */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={12}>
          <Card title={<span><BarChartOutlined style={{ marginRight: '8px' }} />模型性能对比</span>} className="chart-container">
            <ReactECharts
              option={getModelPerformanceChartOption()}
              style={{ width: '100%', height: '350px' }}
              notMerge={true}
              lazyUpdate={true}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title={<span><PieChartOutlined style={{ marginRight: '8px' }} />用户活跃度分布</span>} className="chart-container">
            <ReactECharts
              option={getUserActivityChartOption()}
              style={{ width: '100%', height: '350px' }}
              notMerge={true}
              lazyUpdate={true}
            />
          </Card>
        </Col>
      </Row>

      {/* 🔥 新增：实时数据监控 */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card title={<span><LineChartOutlined style={{ marginRight: '8px' }} />实时数据监控</span>} className="chart-container">
            <ReactECharts
              option={getRealTimeMonitorChartOption()}
              style={{ width: '100%', height: '300px' }}
              notMerge={true}
              lazyUpdate={true}
            />
          </Card>
        </Col>
      </Row>

      {/* 🔥 新增：热力图 - 用户活动时间分布 */}
      <Row gutter={16}>
        <Col span={24}>
          <Card title={<span><BarChartOutlined style={{ marginRight: '8px' }} />用户活动热力图</span>} className="chart-container">
            <ReactECharts
              option={getActivityHeatmapOption()}
              style={{ width: '100%', height: '400px' }}
              notMerge={true}
              lazyUpdate={true}
            />
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default Dashboard
